/**
 * Why local ASR did not carry a unit of work. Most members are phrase-scoped — a phrase
 * that could have run locally went to the server instead — while INFERENCE_FAILURE is the
 * one activity-level latch (the runtime ended local ASR for the whole session).
 *
 * Lives here rather than in SpeechStream because its keys are consumed on
 * opposite sides of the warmup/runtime split (TT-1231): HANDLER_DECLINED by
 * asr/localAsrWarmup, RUNTIME_NOT_READY and PHONEME_PLACEMENT by SpeechStream's
 * startLocalAsrPhrase, and INFERENCE_FAILURE by LocalPlacementAsrHandler's teardown /
 * SpeechStream's onError latch.
 * Exporting it from SpeechStream would make the warmup module import SpeechStream,
 * and splitting it in two would fragment a vocabulary fleet analysis reads as one set.
 *
 * A DEPENDENCY-FREE leaf on purpose. Production code imports it via
 * localAsrLifecycle's re-export; test files that replace localAsrLifecycle with a
 * jest.mock factory pull the real vocabulary from HERE with jest.requireActual —
 * requireActual evaluates the whole target module, and this one has no import
 * chain, unlike localAsrLifecycle whose ../logger import drags aws-amplify into
 * any suite that has not mocked it. Do not add imports to this file.
 */
export const LOCAL_ASR_FALLBACK_REASON = Object.freeze({
  RUNTIME_NOT_READY: 'runtimeNotReady',
  HANDLER_DECLINED: 'handlerDeclined',
  // Phrase-scoped: a tutor upAndDown phrase scored against server-Kaldi phoneme
  // tokens ("ch_phon ih_phon k_phon chick"), which local ASR cannot emit, so the
  // phrase deliberately stays on the server (TT-1285).
  PHONEME_PLACEMENT: 'kaldiPhonemePlacement',
  // Phrase-scoped: this phrase has no usable biased graph (its per-phrase HCLG never
  // downloaded even after the between-phrase re-attempt, its FST failed to build, or the
  // runtime cannot bind a graph). The Vosk word-list grammar fallback is defective, so
  // the phrase is routed to server Kaldi instead of decoding wrong locally (TT-1283).
  // The specific sub-reason (missing / buildFailed / runtimeUnsupported) is carried by
  // the engine's own graphs.* lifecycle events, not conflated into this route marker.
  GRAPH_UNAVAILABLE: 'graphUnavailable',
  // Activity-level latch: MAX_CONSECUTIVE_INFERENCE_FAILURES ended local ASR for the
  // session. Hyphenated spelling predates this enum (it is the handler's onError
  // reason and is already in recorded timelines), so it is preserved rather than
  // camelCased — the flat activity summary and the timeline must stay one vocabulary.
  INFERENCE_FAILURE: 'inference-failure',
});
